/**
 * DESIGO® / RTCOM® identifier grammar (ADR-0006).
 *
 *   DSG-<CLASS>-<serial>-<check>
 *
 * The check character is the Damm check digit over the SERIAL BODY ONLY (not the prefix), exactly as the
 * field apps compute it (SX-A2 §3.2, SX-C1 §3.2, SX-B1 §10.3). Verified against 2,100 printed bottle labels and
 * 383 customer stickers. The prefix is therefore NOT protected by the checksum: callers must check the class
 * against what they expected ("wrong entity type" guard).
 *
 * QR payloads are plain text, never URLs, and never carry a trademark symbol.
 */

/** Standard order-10 weakly totally anti-symmetric quasigroup (Damm). Identical to the field apps' table. */
const DAMM_TABLE: readonly (readonly number[])[] = [
  [0, 3, 1, 7, 5, 9, 8, 6, 4, 2],
  [7, 0, 9, 2, 1, 5, 4, 8, 6, 3],
  [4, 2, 0, 6, 8, 7, 1, 3, 5, 9],
  [1, 7, 5, 0, 9, 8, 3, 4, 2, 6],
  [6, 1, 2, 3, 0, 4, 5, 9, 7, 8],
  [3, 6, 7, 4, 2, 0, 9, 5, 8, 1],
  [5, 8, 6, 9, 7, 2, 0, 1, 3, 4],
  [8, 9, 4, 5, 3, 6, 2, 0, 1, 7],
  [9, 4, 3, 8, 6, 1, 7, 2, 0, 5],
  [2, 5, 8, 1, 4, 3, 6, 7, 9, 0],
];

/** Damm check digit of a string of decimal digits. */
export function dammCheckDigit(digits: string): number {
  if (!/^\d+$/.test(digits)) throw new Error(`Damm input must be decimal digits, got "${digits}"`);
  let interim = 0;
  for (const ch of digits) {
    const row = DAMM_TABLE[interim];
    const next = row?.[ch.charCodeAt(0) - 48];
    if (next === undefined) throw new Error('Damm table lookup failed');
    interim = next;
  }
  return interim;
}

/** True when `digitsWithCheck` (serial followed by its check digit) validates. */
export function dammValidate(digitsWithCheck: string): boolean {
  if (!/^\d{2,}$/.test(digitsWithCheck)) return false;
  return dammCheckDigit(digitsWithCheck) === 0;
}

/**
 * Identifier classes and their serial widths.
 * BTL bottle · BAR barrel · CHL chiller compartment · CHM chiller machine/cabinet · VEH vehicle · FRM site (farm/plant/hub)
 * BCH batch · FLL filling lot · JDH customer (city code as class; 5-digit serial).
 */
export const DSG_CLASSES = {
  BTL: { serialDigits: 6, entity: 'bottle' },
  BAR: { serialDigits: 6, entity: 'barrel' },
  CHL: { serialDigits: 6, entity: 'chiller_compartment' },
  CHM: { serialDigits: 6, entity: 'chiller_unit' },
  VEH: { serialDigits: 6, entity: 'vehicle' },
  FRM: { serialDigits: 6, entity: 'site' },
  BCH: { serialDigits: 6, entity: 'batch' },
  FLL: { serialDigits: 6, entity: 'filling_lot' },
  JDH: { serialDigits: 5, entity: 'customer' },
  // v7 CORE-9: every class the ERP keeps (core.qr_classes), not only the nine the field apps read.
  PLT: { serialDigits: 6, entity: 'plant' },
  RTE: { serialDigits: 6, entity: 'route' },
  DEV: { serialDigits: 6, entity: 'device' },
  CUS: { serialDigits: 6, entity: 'door' },
  TNK: { serialDigits: 6, entity: 'tank' },
  HUB: { serialDigits: 6, entity: 'delivery_point' },
  JAR: { serialDigits: 6, entity: 'jar' },
  CRT: { serialDigits: 6, entity: 'crate' },
  CAN: { serialDigits: 6, entity: 'milk_can' },
  CLD: { serialDigits: 6, entity: 'cold_room' },
} as const;

export type DsgClass = keyof typeof DSG_CLASSES;

export type DsgParseResult =
  | { ok: true; cls: DsgClass; serial: string; check: number; payload: string; baseCode: string }
  | { ok: false; reason: 'NOT_DESIGO' | 'UNKNOWN_CLASS' | 'BAD_SHAPE' | 'DAMAGED'; payload: string };

/**
 * Parse a scanned or typed payload. Whitespace is trimmed and case is normalised (the field apps forgive both).
 * Outcomes mirror the field classifier: not a DESIGO code · DESIGO but unknown class · damaged (bad check).
 */
export function parseDsg(raw: string): DsgParseResult {
  const payload = raw.trim().toUpperCase();
  if (!payload.startsWith('DSG-')) return { ok: false, reason: 'NOT_DESIGO', payload };
  const match = /^DSG-([A-Z]{3})-(\d+)-(\d)$/.exec(payload);
  if (!match) return { ok: false, reason: 'BAD_SHAPE', payload };
  const cls = match[1] as string;
  const serial = match[2] as string;
  const check = Number(match[3]);
  if (!(cls in DSG_CLASSES)) return { ok: false, reason: 'UNKNOWN_CLASS', payload };
  const spec = DSG_CLASSES[cls as DsgClass];
  if (serial.length !== spec.serialDigits) return { ok: false, reason: 'BAD_SHAPE', payload };
  if (dammCheckDigit(serial) !== check) return { ok: false, reason: 'DAMAGED', payload };
  return { ok: true, cls: cls as DsgClass, serial, check, payload, baseCode: `DSG-${cls}-${serial}` };
}

/** Build the canonical payload for a class and numeric serial. */
export function formatDsg(cls: DsgClass, serial: number): string {
  const spec = DSG_CLASSES[cls];
  if (!Number.isInteger(serial) || serial < 0) throw new Error('Serial must be a non-negative integer');
  const body = String(serial).padStart(spec.serialDigits, '0');
  if (body.length !== spec.serialDigits) throw new Error(`Serial exceeds ${spec.serialDigits} digits`);
  return `DSG-${cls}-${body}-${dammCheckDigit(body)}`;
}

/** Customer code (the master key, WITHOUT check digit): DSG-JDH-00010. */
export const CUSTOMER_CODE_PATTERN = /^DSG-([A-Z]{2,4})-(\d{5})$/;

/** Customer sticker payload = customer code + Damm digit over the 5-digit number: DSG-JDH-00010-1. */
export function customerStickerPayload(customerCode: string): string {
  const match = CUSTOMER_CODE_PATTERN.exec(customerCode.trim().toUpperCase());
  if (!match) throw new Error(`Not a customer code: "${customerCode}"`);
  const digits = match[2] as string;
  return `DSG-${match[1]}-${digits}-${dammCheckDigit(digits)}`;
}

/** Barrel entity key used by the field app: BR-000123 (client master key; distinct from the QR payload). */
export const BARREL_KEY_PATTERN = /^BR-\d{6}$/;

/** Rider operational code: RID-001. */
export const RIDER_CODE_PATTERN = /^RID-\d{3}$/;

/** Fleet standing-order / delivery line key: {customerCode}_{variantCode}. */
export function lineKey(customerCode: string, variantCode: string): string {
  return `${customerCode}_${variantCode}`;
}

/** Field-app ledger address: CP- · BT- · CM- · DB- · BR- · TR- · TL- · CR- · RS- (split at the FIRST hyphen). */
export const LEDGER_PREFIXES = ['CP', 'BT', 'CM', 'DB', 'BR', 'TR', 'TL', 'CR', 'RS'] as const;
export type LedgerPrefix = (typeof LEDGER_PREFIXES)[number];

export function parseLedgerAddress(ref: string): { prefix: LedgerPrefix; id: string } | null {
  const idx = ref.indexOf('-');
  if (idx <= 0) return null;
  const prefix = ref.slice(0, idx) as LedgerPrefix;
  const id = ref.slice(idx + 1);
  if (!LEDGER_PREFIXES.includes(prefix) || id.length === 0 || id.includes('|')) return null;
  return { prefix, id };
}
